import React, { forwardRef, useState } from 'react'
import dayjs from 'dayjs'
import { Link, Route, Routes } from 'react-router-dom'
import { useOrderedGalleriesGetAllQuery } from 'shared/extended'
import Container from 'src/components/Container'
import PageHeader from 'src/components/PageHeader'
import Button from 'src/components/Button'
import GalleryView from './view'
import GalleryForm from './form'
import VideoLightbox from './VideoLightbox'
import {
	GalleryCard,
	GalleryCardContent,
	GalleryCardTitle,
	GalleryCardSubTitle,
	GalleryCardImage,
	GalleryButtonsContainer,
	GalleryHeaderContainer,
	Edit,
	Delete
} from './styled'
import { useDisableGalleryMutation, GalleriesGetAllQuery, useUpdateGalleryOrderMutation } from 'shared/schema'
import { useUserRoles } from 'src/components/Roles'

import { SortableGrid } from './sortable-grid'

const mutationConfig = {
	refetchQueries: ['galleriesGetAll'],
	awaitRefetchQueries: true
}
type FullGalleryCardProps = { style?: any; data: GalleriesGetAllQuery['galleries'][number] }
const FullGalleryCard = forwardRef<HTMLDivElement, FullGalleryCardProps>(
	({ style, ...props }: FullGalleryCardProps, ref) => {
		const inlineStyles = {
			height: 200,
			position: 'relative',
			...style
		}

		const [disableGallery] = useDisableGalleryMutation(mutationConfig)
		const g = props.data
		const { isGalleryAdmin: canEdit } = useUserRoles()
		const [showVideo, setShowVideo] = useState(false)
		const isVideo = !!g.videoUpload

		const cardContent = (
			<>
				<GalleryCardImage
					style={{
						backgroundImage: `url(${g.thumbUrl || g.videoThumbnail?.publicUrl || ''})`
					}}
				/>
				{isVideo && (
					<div style={{
						position: 'absolute', top: '8px', left: '8px', zIndex: 2,
						backgroundColor: 'rgba(0,0,0,0.7)', color: '#fff', padding: '2px 8px',
						borderRadius: '4px', fontSize: '11px', fontWeight: 'bold', letterSpacing: '0.5px'
					}}>
						VIDEO
					</div>
				)}
				<GalleryCardContent>
					<GalleryCardTitle>{g.title}</GalleryCardTitle>
					<GalleryCardSubTitle>
						{isVideo ? 'Video' : `${(g.children as number[]).length} photos`}
						{g.date && ' on ' + dayjs(g.date.split('T')[0]).format('LL')}
					</GalleryCardSubTitle>
				</GalleryCardContent>
			</>
		)

		return (
			<div ref={ref} style={inlineStyles} {...props}>
				<GalleryCard key={g.id}>
					{isVideo ? (
						<div onClick={() => setShowVideo(true)} style={{ cursor: 'pointer', height: '100%' }}>
							{cardContent}
						</div>
					) : (
						<Link to={'/gallery/' + g.id} key={g.id}>
							{cardContent}
						</Link>
					)}
					{canEdit && (
						<GalleryButtonsContainer>
							<Link to={`${g.id}/publish`}>
								<Edit />
							</Link>
							<Delete
								onClick={() => {
									if (confirm('Are you sure you want to delete this album?')) {
										disableGallery({
											variables: {
												id: g.id
											}
										})
									}
								}}
							/>
						</GalleryButtonsContainer>
					)}
				</GalleryCard>
				{showVideo && g.videoUpload && (
					<VideoLightbox
						src={g.videoUpload.publicUrl}
						poster={g.videoThumbnail?.publicUrl || ''}
						onClose={() => setShowVideo(false)}
						caption={g.title}
					/>
				)}
			</div>
		)
	}
)

function GalleryList() {
	const { data } = useOrderedGalleriesGetAllQuery()
	const { isGalleryAdmin: canEdit } = useUserRoles()

	return (
		<>
			<GalleryHeaderContainer>
				<PageHeader primaryColor={false}>Albums</PageHeader>
				{canEdit && (
					<Link to="/gallery/publish">
						<Button $autoWidth mode="secondary">
							Create New Gallery
						</Button>
					</Link>
				)}
			</GalleryHeaderContainer>
			<SortableGrid
				gridColumns={3}
				GridItemComponent={FullGalleryCard}
				reorderHook={useUpdateGalleryOrderMutation}
				reorderHookConfig={mutationConfig}
				initialItems={data?.galleries}
			/>
		</>
	)
}

function GalleryRoute() {
	return (
		<Container>
			<Routes>
				<Route path="/" element={<GalleryList />} />
				<Route path="/publish" element={<GalleryForm />} />
				<Route path="/:galleryId" element={<GalleryView />} />
				<Route path="/:galleryId/publish" element={<GalleryForm />} />
			</Routes>
		</Container>
	)
}

export default GalleryRoute
